<template>
  <div :class="{fullscreen:fullscreen}" class="tinymce-container" :style="{width:containerWidth}">
    <textarea :id="tinymceId"  />
    <div v-if="showImageUploadBtn" class="editor-custom-btn-container">
      <editorImage color="#1890ff" class="editor-upload-btn" @successCBK="imageSuccessCBK" />
    </div>
  </div>
</template>

<script>
/**
 * docs:
 * https://panjiachen.github.io/vue-element-admin-site/feature/component/rich-editor.html#tinymce
 */
import editorImage from './components/EditorImage'
import plugins from './plugins'
import toolbar from './toolbar'
import load from './dynamicLoadScript'
import { uploadImageFile } from '@/api/upload'
import {getPresignDownloadUrl} from "../../api/upload";
import {uploadUtil} from "@/utils/uploadCommon";
const tinymceCDN = process.env.NODE_ENV === 'production' ? '/2c/static/tinymce/tinymce.min.js' : '../../../static/tinymce/tinymce.min.js'

export default {
  name: 'Tinymce',
  components: { editorImage },
  props: {
    id: {
      type: String,
      default: function() {
        return 'vue-tinymce-' + +new Date() + ((Math.random() * 1000).toFixed(0) + '')
      }
    },
    value: {
      type: String,
      default: ''
    },
    toolbar: {
      type: Array,
      required: false,
      default() {
        return []
      }
    },
    menubar: {
      type: String,
      default: 'file edit insert view format table'
    },
    height: {
      type: [Number, String],
      required: false,
      default: 360
    },
    width: {
      type: [Number, String],
      required: false,
      default: 'auto'
    },
    readonly: {
      type: [Number, Boolean],
      default: false
    },
    // 新增：是否显示工具栏
    showToolbar: {
      type: Boolean,
      default: true
    },
    // 新增：是否显示菜单栏
    showMenubar: {
      type: Boolean,
      default: false  // 默认为null，使用原有的menubar配置
    },
    // 新增：是否显示状态栏
    showStatusbar: {
      type: Boolean,
      default: true
    },
    // 新增：是否显示图片上传按钮
    showImageUploadBtn: {
      type: Boolean,
      default: false
    },
    // 新增：最小化模式（快捷设置）
    minimalMode: {
      type: Boolean,
      default: false
    },
    // 新增：简化工具栏（只保留基本功能）
    simpleToolbar: {
      type: Boolean,
      default: true
    },
    // 新增：是否隐藏工具栏按钮文字
    hideToolbarLabels: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hasChange: false,
      hasInit: false,
      tinymceId: this.id,
      fullscreen: false,
      languageTypeList: {
        'en': 'en',
        'zh': 'zh_CN',
        'es': 'es_MX',
        'ja': 'ja'
      }
    }
  },
  computed: {
    containerWidth() {
      const width = this.width
      if (/^[\d]+(\.[\d]+)?$/.test(width)) { // matches `100`, `'100'`
        return `${width}px`
      }
      return width
    },
    // 计算最终的工具栏配置
    finalToolbar() {
      if (this.minimalMode || !this.showToolbar) {
        return false
      }
      if (this.simpleToolbar) {
        // 简化工具栏：只保留基本功能（使用图标，隐藏文字）
        return 'undo redo | bold italic | alignleft aligncenter alignright | image  | removeformat'
      }
      return this.toolbar.length > 0 ? this.toolbar : toolbar
    },
    // 计算最终的菜单栏配置
    finalMenubar() {
      if (this.minimalMode) {
        return false
      }
      if (this.showMenubar === false) {
        return false
      }
      if (this.showMenubar === true) {
        return this.menubar
      }
      // 保持原有的默认行为
      return this.menubar
    },
    // 计算最终的状态栏配置
    finalStatusbar() {
      return !(this.minimalMode || !this.showStatusbar)
    },
    // 计算是否显示元素路径
    finalElementPath() {
      return !(this.minimalMode)
    },
    // 计算是否显示浏览器自带的右键菜单
    finalContextmenu() {
      return !(this.minimalMode)
    }
  },
  watch: {
    value(val) {
      if (!this.hasChange && this.hasInit) {
        this.$nextTick(() =>
          window.tinymce.get(this.tinymceId).setContent(val || ''))
      }
    }
  },
  mounted() {
    this.init()
  },
  activated() {
    if (window.tinymce) {
      this.initTinymce()
    }
  },
  deactivated() {
    this.destroyTinymce()
  },
  destroyed() {
    this.destroyTinymce()
  },
  methods: {
    init() {
      console.log(tinymceCDN,"dasdas")
      // dynamic load tinymce from cdn
      load(tinymceCDN, (err) => {
        if (err) {
          this.$message.error(err.message)
          return
        }
        this.initTinymce()
      })
    },
    async initTinymce() {
      const _this = this
      window.tinymce.init({
        selector: `#${this.tinymceId}`,
        readonly: this.readonly,
        language: this.languageTypeList['zh'],
        height: this.height,
        body_class: 'panel-body ',
        paste_word_valid_elements: '*[*]', // word需要它
        paste_data_images: true, // 粘贴的同时能把内容里的图片自动上传，非常强力的功能
        paste_convert_word_fake_lists: false, // 插入word文档需要该属性
        images_upload_base_path: 'C:\Users\Houys\Desktop',
        object_resizing: false,

        // 使用计算属性
        toolbar: this.finalToolbar,
        menubar: this.finalMenubar,
        statusbar: this.finalStatusbar,
        elementpath: this.finalElementPath,
        contextmenu: this.finalContextmenu,

        file_picker_callback: 'media',
        plugins: plugins,
        // images_upload_url: location.protocol + '//' + location.host + '/api-2c/oss/uploadImgToOss',
        end_container_on_empty_block: true,
        powerpaste_word_import: 'clean',
        code_dialog_height: 450,
        code_dialog_width: 1000,
        advlist_bullet_styles: 'square',
        advlist_number_styles: 'default',
        imagetools_cors_hosts: ['www.tinymce.com', 'codepen.io'],
        default_link_target: '_blank',
        link_title: false,
        nonbreaking_force_tab: true, // inserting nonbreaking space &nbsp; need Nonbreaking Space Plugin

        // 在简化模式下禁用一些格式化选项
        block_formats: this.simpleToolbar ? '' : undefined,
        style_formats: this.simpleToolbar ? [] : undefined,
        fontsize_formats: this.simpleToolbar ? '' : undefined,
        font_formats: this.simpleToolbar ? '' : undefined,

        // 设置工具栏样式，隐藏按钮文字
        toolbar_mode: this.hideToolbarLabels ? 'sliding' : 'wrap',
        // 强制使用图标，隐藏文字标签
        icons: 'small',

        init_instance_callback: editor => {
          if (_this.value) {
            editor.setContent(_this.value)
          }
          _this.hasInit = true
          editor.on('NodeChange Change KeyUp SetContent', () => {
            this.hasChange = true
            this.$emit('input', editor.getContent())
          })

          // 在编辑器初始化完成后，强制隐藏工具栏按钮文字
          if (_this.hideToolbarLabels || _this.minimalMode) {
            _this.$nextTick(() => {
              const toolbarEl = document.querySelector(`#${_this.tinymceId}_toolbar`);
              if (toolbarEl) {
                // 找到所有工具栏按钮并隐藏文字
                const buttons = toolbarEl.querySelectorAll('.mce-btn');
                buttons.forEach(btn => {
                  const label = btn.querySelector('.mce-txt');
                  if (label) {
                    label.style.display = 'none';
                  }
                });
              }
            });
          }
        },
        setup(editor) {
          editor.on('FullscreenStateChanged', (e) => {
            _this.fullscreen = e.state
          })
        },
        convert_urls: false,
        images_upload_handler:  async (blobInfo, successFun, failure) => {
          const file = blobInfo.blob()
          const formData = new FormData()
          const files = await uploadUtil(file)
          formData.append("imgFile", files);
          uploadImageFile(formData).then(res => {
            getPresignDownloadUrl(res.data).then(res => {
              if (res.code == 200) {
                console.log(res.data)
                successFun(res.data)
              } else {
                failure("上传失败")
              }
            })
          })
        },
      })
    },

    destroyTinymce() {
      const tinymce = window.tinymce.get(this.tinymceId)
      if (this.fullscreen) {
        tinymce.execCommand('mceFullScreen')
      }

      if (tinymce) {
        tinymce.destroy()
      }
    },
    setContent(value) {
      window.tinymce.get(this.tinymceId).setContent(value)
    },
    getContent() {
      window.tinymce.get(this.tinymceId).getContent()
    },
    imageSuccessCBK(arr) {
      const _this = this
      arr.forEach(v => {
        window.tinymce.get(_this.tinymceId).insertContent(`<img style="max-height: 360px" src="${v.url}" >`)
      })
    }
  }
}
</script>

<style scoped>
.tinymce-container {
  position: relative;
  line-height: normal;
}
.tinymce-container>>>.mce-tinymce {
  box-shadow: 0 0 20px rgba(100, 100, 100, .1);
  border: 1px solid #DCDFE6;
}
.tinymce-container>>>span.mce-last {
  display: none;
}
.tinymce-container>>>.mce-panel {
  border-color: #DCDFE6;
}
.tinymce-container>>>.mce-top-part::before {
  box-shadow: unset;
}
.tinymce-container>>>.mce-fullscreen {
  z-index: 10000;
}
.tinymce-textarea {
  visibility: hidden;
  z-index: -1;
}



.editor-custom-btn-container {
  position: absolute;
  right: 4px;
  top: 4px;
  /*z-index: 2005;*/
}
.fullscreen .editor-custom-btn-container {
  z-index: 10000;
  position: fixed;
}
.editor-upload-btn {
  display: inline-block;
}
</style>



// WEBPACK FOOTER //
// src/components/Tinymce/index.vue